import { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Card } from "../ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  MoreVertical,
  Eye,
  CheckCircle,
  PauseCircle,
  Clock,
  Calendar,
  User,
  Store,
  Search,
  Filter,
  Trash2,
} from "lucide-react";
import { Owner } from "../../lib/adminApi";
import { format } from "date-fns";
import { es } from "date-fns/locale";

interface ShopsTableProps {
  owners: Owner[];
  loading: boolean;
  onViewDetails: (owner: Owner) => void;
  onActivate: (owner: Owner) => void;
  onSuspend: (owner: Owner) => void;
  onExtendTrial: (owner: Owner) => void;
  onDelete: (owner: Owner) => void;
  onSearch: (query: string) => void;
  onFilterChange: (filter: string) => void;
  onClearSearch: () => void;
  currentFilter: string;
  searchQuery?: string;
  // Mejora 2: Ordenamiento
  onSort?: (column: string) => void;
  sortColumn?: string;
  sortDirection?: "asc" | "desc";
}

export function ShopsTable({
  owners,
  loading,
  onViewDetails,
  onActivate,
  onSuspend,
  onExtendTrial,
  onDelete,
  onSearch,
  onFilterChange,
  onClearSearch,
  currentFilter,
  searchQuery: externalSearchQuery,
  // Mejora 2: Ordenamiento
  onSort,
  sortColumn,
  sortDirection,
}: ShopsTableProps) {
  const [internalSearchQuery, setInternalSearchQuery] = useState(
    externalSearchQuery || "",
  );

  // Sincronizar con la prop externa cuando cambia
  useEffect(() => {
    if (externalSearchQuery !== undefined) {
      setInternalSearchQuery(externalSearchQuery);
    }
  }, [externalSearchQuery]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInternalSearchQuery(value);
    onSearch(value);
  };

  // Mejora 2: Manejador de ordenamiento
  const handleSort = (column: string) => {
    if (onSort) {
      onSort(column);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "active":
        return (
          <Badge className="bg-green-100 text-green-800 hover:bg-green-100 border-green-200">
            <CheckCircle className="h-3 w-3 mr-1" />
            Activo
          </Badge>
        );
      case "trial":
        return (
          <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100 border-yellow-200">
            <Clock className="h-3 w-3 mr-1" />
            Trial
          </Badge>
        );
      case "suspended":
        return (
          <Badge className="bg-red-100 text-red-800 hover:bg-red-100 border-red-200">
            <PauseCircle className="h-3 w-3 mr-1" />
            Suspendido
          </Badge>
        );
      case "canceled":
        return (
          <Badge variant="outline" className="text-gray-600 border-gray-300">
            Cancelado
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "N/A";
    try {
      return format(new Date(dateString), "dd/MM/yyyy", { locale: es });
    } catch {
      return "Fecha inválida";
    }
  };

  // Mejora 1: Fechas de vencimiento destacadas
  const getDaysRemaining = (endDate: string | null, status: string) => {
    if (!endDate || status !== "trial") return { text: null, className: "" };

    try {
      const end = new Date(endDate);
      const now = new Date();
      const diffTime = end.getTime() - now.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays <= 0)
        return { text: "Expirado", className: "text-red-600 font-bold" };
      if (diffDays <= 3)
        return {
          text: `${diffDays} días`,
          className: "text-orange-600 font-bold",
        };
      if (diffDays <= 7)
        return { text: `${diffDays} días`, className: "text-yellow-600" };
      return { text: `${diffDays} días`, className: "text-green-600" };
    } catch {
      return { text: null, className: "" };
    }
  };

  // Mejora 7: Indicador de carga por esqueleto
  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="animate-pulse">
            <div className="h-20 bg-gray-200 rounded-lg flex items-center px-4">
              <div className="w-1/4 h-5 bg-gray-300 rounded mr-4"></div>
              <div className="w-1/6 h-5 bg-gray-300 rounded mr-4"></div>
              <div className="w-1/6 h-5 bg-gray-300 rounded mr-4"></div>
              <div className="w-1/6 h-5 bg-gray-300 rounded mr-4"></div>
              <div className="w-1/6 h-5 bg-gray-300 rounded"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  const showEmptyState = owners.length === 0;

  return (
    <div className="space-y-4">
      {/* Filtros y búsqueda */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Buscar por nombre del negocio, dueño o email..."
            value={internalSearchQuery}
            onChange={handleSearch}
            className="pl-10 pr-10"
          />
          {internalSearchQuery && (
            <button
              type="button"
              onClick={onClearSearch}
              className="absolute right-3 top-3 h-4 w-4 text-gray-400 hover:text-gray-600"
              aria-label="Limpiar búsqueda"
            >
              ✕
            </button>
          )}
        </div>

        <div className="flex gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                <Filter className="h-4 w-4 mr-2" />
                {currentFilter === "all"
                  ? "Todos"
                  : currentFilter === "active"
                    ? "Activos"
                    : currentFilter === "trial"
                      ? "Trials"
                      : currentFilter === "suspended"
                        ? "Suspendidos"
                        : "Todos"}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => onFilterChange("all")}>
                Todos los negocios
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onFilterChange("active")}>
                <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                Activos
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onFilterChange("trial")}>
                <Clock className="h-4 w-4 mr-2 text-yellow-600" />
                En Trial
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onFilterChange("suspended")}>
                <PauseCircle className="h-4 w-4 mr-2 text-red-600" />
                Suspendidos
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Estado vacío o tabla */}
      {showEmptyState ? (
        <Card className="p-8 text-center">
          <Store className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            No se encontraron negocios
          </h3>
          <p className="text-gray-600 mb-4">
            {currentFilter !== "all"
              ? `No hay negocios con estado "${currentFilter}"`
              : "No hay negocios registrados en el sistema"}
          </p>
          <div className="flex justify-center gap-2">
            <Button
              variant="outline"
              onClick={onClearSearch}
              disabled={!internalSearchQuery && currentFilter === "all"}
            >
              Limpiar búsqueda
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                onFilterChange("all");
                if (internalSearchQuery) {
                  onClearSearch();
                }
              }}
            >
              Ver todos
            </Button>
          </div>
        </Card>
      ) : (
        <>
          {/* Tabla con ordenamiento */}
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  {/* Mejora 2: TableHead clickeables con indicadores de orden */}
                  <TableHead
                    onClick={() => handleSort("businessName")}
                    className="cursor-pointer hover:bg-gray-100"
                  >
                    Negocio{" "}
                    {sortColumn === "businessName" &&
                      (sortDirection === "asc" ? " ↑" : " ↓")}
                  </TableHead>
                  <TableHead
                    onClick={() => handleSort("accountStatus")}
                    className="cursor-pointer hover:bg-gray-100"
                  >
                    Estado{" "}
                    {sortColumn === "accountStatus" &&
                      (sortDirection === "asc" ? " ↑" : " ↓")}
                  </TableHead>
                  <TableHead
                    onClick={() => handleSort("ownerName")}
                    className="cursor-pointer hover:bg-gray-100"
                  >
                    Dueño / Contacto{" "}
                    {sortColumn === "ownerName" &&
                      (sortDirection === "asc" ? " ↑" : " ↓")}
                  </TableHead>
                  <TableHead
                    onClick={() => handleSort("createdAt")}
                    className="cursor-pointer hover:bg-gray-100"
                  >
                    Registro{" "}
                    {sortColumn === "createdAt" &&
                      (sortDirection === "asc" ? " ↑" : " ↓")}
                  </TableHead>
                  <TableHead>Días Trial</TableHead>
                  <TableHead
                    onClick={() => handleSort("totalRevenue")}
                    className="cursor-pointer hover:bg-gray-100"
                  >
                    Estadísticas{" "}
                    {sortColumn === "totalRevenue" &&
                      (sortDirection === "asc" ? " ↑" : " ↓")}
                  </TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {owners.map((owner) => {
                  const daysRemaining = getDaysRemaining(
                    owner.trialEndDate,
                    owner.accountStatus,
                  );
                  return (
                    <TableRow key={owner.id} className="hover:bg-gray-50">
                      {/* Mejora 4: Badge de multi-tienda */}
                      <TableCell>
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="font-medium">
                            {owner.businessName}
                          </div>
                          {owner.shopsCount && owner.shopsCount > 1 && (
                            <Badge
                              variant="outline"
                              className="bg-purple-50 text-purple-700 border-purple-200 text-xs"
                            >
                              {owner.shopsCount} tiendas
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          ID: {owner.id.substring(0, 8)}...
                        </div>
                      </TableCell>

                      {/* Celda: Estado */}
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          {getStatusBadge(owner.accountStatus)}
                          {owner.accountStatus === "trial" &&
                            daysRemaining.text && (
                              <span
                                className={`text-xs font-medium ${daysRemaining.className}`}
                              >
                                {daysRemaining.text}
                              </span>
                            )}
                        </div>
                      </TableCell>

                      {/* Celda: Dueño / Contacto */}
                      <TableCell>
                        <div className="flex items-center">
                          <User className="h-3 w-3 text-gray-400 mr-1" />
                          <span>{owner.ownerName}</span>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {owner.email}
                        </div>
                        {owner.phone && (
                          <div className="text-xs text-gray-500">
                            {owner.phone}
                          </div>
                        )}
                      </TableCell>

                      {/* Celda: Registro */}
                      <TableCell>
                        <div className="text-sm">
                          {formatDate(owner.createdAt)}
                        </div>
                      </TableCell>

                      {/* Celda: Días Trial */}
                      <TableCell>
                        {owner.accountStatus === "trial" ? (
                          <div className="text-sm">
                            <div className="flex items-center">
                              <Calendar className="h-3 w-3 text-gray-400 mr-1" />
                              {formatDate(owner.trialEndDate)}
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500">N/A</span>
                        )}
                      </TableCell>

                      {/* Celda: Estadísticas */}
                      <TableCell>
                        <div className="text-sm space-y-1">
                          <div className="flex items-center justify-between">
                            <span className="text-gray-600">Tiendas:</span>
                            <span className="font-medium">
                              {owner.shopsCount || 1}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-600">Productos:</span>
                            <span className="font-medium">
                              {owner.totalProducts || 0}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-600">
                              Ventas totales:
                            </span>
                            <span className="font-medium">
                              {owner.totalSales || 0}
                            </span>
                          </div>
                          {owner.totalRevenue !== undefined && (
                            <div className="flex items-center justify-between font-medium text-primary">
                              <span>Ingresos:</span>
                              <span>
                                {Number(owner.totalRevenue).toFixed(2)} CUP
                              </span>
                            </div>
                          )}
                        </div>
                      </TableCell>

                      {/* Celda: Acciones */}
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => onViewDetails(owner)}
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              Ver detalles
                            </DropdownMenuItem>

                            {owner.accountStatus === "suspended" && (
                              <DropdownMenuItem
                                onClick={() => onActivate(owner)}
                              >
                                <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                                Activar cuenta
                              </DropdownMenuItem>
                            )}

                            {owner.accountStatus === "active" && (
                              <DropdownMenuItem
                                onClick={() => onSuspend(owner)}
                              >
                                <PauseCircle className="h-4 w-4 mr-2 text-red-600" />
                                Suspender
                              </DropdownMenuItem>
                            )}

                            {owner.accountStatus === "trial" && (
                              <>
                                <DropdownMenuItem
                                  onClick={() => onExtendTrial(owner)}
                                >
                                  <Clock className="h-4 w-4 mr-2 text-yellow-600" />
                                  Extender trial
                                </DropdownMenuItem>

                                <DropdownMenuItem
                                  onClick={() => onActivate(owner)}
                                >
                                  <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                                  Activar cuenta (pagar)
                                </DropdownMenuItem>

                                <DropdownMenuItem
                                  onClick={() => onSuspend(owner)}
                                >
                                  <PauseCircle className="h-4 w-4 mr-2 text-red-600" />
                                  Suspender cuenta
                                </DropdownMenuItem>
                              </>
                            )}

                            <DropdownMenuItem
                              onClick={() => onDelete(owner)}
                              disabled={owner.accountStatus === "active"}
                              className={`
                                ${
                                  owner.accountStatus === "active"
                                    ? "text-gray-400 cursor-not-allowed"
                                    : "text-red-600 hover:bg-red-50 hover:text-red-700"
                                }
                              `}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Eliminar negocio
                              {owner.accountStatus === "active" && (
                                <span className="ml-2 text-xs">
                                  (No disponible)
                                </span>
                              )}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {/* Resumen */}
          <div className="text-sm text-gray-600">
            Mostrando {owners.length} negocio{owners.length !== 1 ? "s" : ""}
            {currentFilter !== "all" && ` con estado "${currentFilter}"`}
          </div>
        </>
      )}
    </div>
  );
}
